import React, { useEffect, useState, useLayoutEffect } from "react";
import { Button, Card, CardBody, Table, Input, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useLeads from "../../Hooks/useLeads";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LeadFilters from "../../components/FbLeadgen/LeadFilters";
import useStages from "../../Hooks/useStages";
import useProjects from "../../Hooks/useProjects";
import useFbForms from "../../Hooks/useFbForms";
import useFbPages from "../../Hooks/useFbPages";
import AppLoader from "../../components/Common/AppLoader";
import DebouncedSearchInput from "../../components/Common/DebouncedSearchInput";
import MobileLeadCards from "../../components/FbLeadgen/MobileLeadCards";
import LeadActivityModal from "../../components/UserLeads/LeadActivityModal";
import CreateLeadModal from "../../Modal/Leads/CreateLeadModal";
import { toast } from "react-toastify";

const UserLeads = () => {
  const location=useLocation();
  const { userId }=useParams();
  const [refresh,setRefresh]=useState(false);  
  const [createModal,setCreateModal]=useState(false);  
  const isMyLeads= location.pathname==="/my-leads";
  const navigate = useNavigate();
  // const userId = searchParams.get("userId");
  const [isMobile,setIsMobile]=useState(window.innerWidth<=768);

  useEffect(()=>{
    const handleResize=()=>setIsMobile(window.innerWidth<=768);

    window.addEventListener("resize",handleResize);

    return ()=>window.removeEventListener("resize",handleResize);
  },[]);

  const DEFAULT_FILTERS = {
    search: "",
    source: "all",
    page: 1,
    limit: 20,
    stage: "",
    projectId: "",
    formId: "",
    pageId: "",
    sortBy: "createdTime",
    sortOrder: "desc",
  };

  const storageKey = isMyLeads ? "my_leads_filters" : `user_leads_filters_${userId || "default"}`;

  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        return { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
      }
    } catch (e) {}
    return DEFAULT_FILTERS;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (e) {}
  }, [filters, storageKey]);

  const { leads, loading, pagination, fetchLeadsByUserId, fetchMyLeads, userName, updateLeadStage, createLeadActivityLog } = useLeads();

  const [filterModal, setFilterModal] = useState(false);
  const [activityModal, setActivityModal] = useState({ isOpen: false, lead: null });
  const [stageModal, setStageModal] = useState({ isOpen: false, lead: null, newStageId: "", newStageName: "" });
  const [stageComment, setStageComment] = useState("");
  const [updatingStage, setUpdatingStage] = useState(false);

  const handleCallLead = async (e, lead) => {
    e.stopPropagation();
    try {
      const res = await createLeadActivityLog(lead.source, lead._id, { action: "call_initiated" });
      const phone = res?.data?.phone || res?.phone;
      if (phone && !phone.includes("*") && !phone.includes("X")) {
        window.location.href = `tel:${phone}`;
      } else if (res?.data?.telUrl || res?.telUrl) {
        window.location.href = res?.data?.telUrl || res?.telUrl;
      } else {
        toast.warning("Phone number not available");
      }
    } catch (err) {
      toast.error(err.message || "Failed to log call activity");
    }
  };

  const handleWhatsappLead = async (e, lead) => {
    e.stopPropagation();
    try {
      const res = await createLeadActivityLog(lead.source, lead._id, { action: "whatsapp_initiated" });
      const cleanPhone = res?.data?.cleanPhone || res?.cleanPhone || res?.data?.phone || res?.phone;
      const digitsOnly = cleanPhone ? String(cleanPhone).replace(/\D/g, "") : "";

      if (digitsOnly && !cleanPhone.includes("*") && !cleanPhone.includes("X")) {
        window.open(`https://wa.me/${digitsOnly}`, "_blank");
      } else if (res?.data?.whatsappUrl || res?.whatsappUrl) {
        window.open(res?.data?.whatsappUrl || res?.whatsappUrl, "_blank");
      } else {
        toast.warning("Phone number not available");
      }
    } catch (err) {
      toast.error(err.message || "Failed to log WhatsApp activity");
    }
  };

  const openStageChangeModal = (e, lead, newStageId) => {
    e.stopPropagation();
    if (!newStageId) return;
    const selectedStageObj = stages.find((s) => String(s._id) === String(newStageId));
    setStageModal({
      isOpen: true,
      lead,
      newStageId,
      newStageName: selectedStageObj?.name || "Selected Stage",
    });
    setStageComment("");
  };

  const handleConfirmStageUpdate = async () => {
    if (!stageModal.lead || !stageModal.newStageId) return;
    try {
      setUpdatingStage(true);
      await updateLeadStage(
        stageModal.lead.source,
        stageModal.lead._id,
        stageModal.newStageId,
        stageComment
      );
      toast.success("Stage & Comment updated successfully!");
      setStageModal({ isOpen: false, lead: null, newStageId: "", newStageName: "" });
      setRefresh((p) => !p);
    } catch (err) {
      toast.error(err.message || "Failed to update stage");
    } finally {
      setUpdatingStage(false);
    }
  };

  const openActivityModal = (e, lead) => {
    e.stopPropagation();
    setActivityModal({ isOpen: true, lead });
  };

  const { stages = [] } = useStages({ limit: 1000 });
  const { projects = [] } = useProjects({ limit: 1000 });
  const { forms = [] } = useFbForms({ limit: 1000 });
  const { pages = [] } = useFbPages();

  const handleChange = (name, value) =>
    setFilters((p) => ({ ...p, [name]: value, page: 1 }));

  const handleReset = () => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (e) {}
    setFilters(DEFAULT_FILTERS);
  };

  const normalizeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") return val.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  };

  const getActiveFiltersList = () => {
    const list = [];
    if (filters.search) {
      list.push({ key: "search", label: `Search: "${filters.search}"` });
    }
    if (filters.source && filters.source !== "all") {
      const srcObj = [
        { label: "Facebook", value: "facebook" },
        { label: "Google", value: "google" },
        { label: "Generic / Import", value: "generic" },
        { label: "Ai calling", value: "ai_calling" },
        { label: "Ivr calling", value: "ivr_calling" },
        { label: "RCS msg", value: "rcs_msg" },
        { label: "Whatsapp", value: "whatsapp" },
      ].find((s) => s.value === filters.source);
      list.push({ key: "source", label: `Source: ${srcObj?.label || filters.source}` });
    }

    const selectedStages = normalizeArray(filters.stage);
    selectedStages.forEach((stgId) => {
      const stg = stages.find((s) => s._id === stgId);
      list.push({ key: "stage", valId: stgId, label: `Stage: ${stg?.name || stgId}` });
    });

    const selectedProjects = normalizeArray(filters.projectId);
    selectedProjects.forEach((prjId) => {
      const prj = projects.find((p) => p._id === prjId);
      list.push({ key: "projectId", valId: prjId, label: `Project: ${prj?.projectName || prjId}` });
    });

    const selectedForms = normalizeArray(filters.formId);
    selectedForms.forEach((frmId) => {
      const frm = forms.find((f) => f.formId === frmId);
      list.push({ key: "formId", valId: frmId, label: `Form: ${frm?.formName || frmId}` });
    });

    const selectedPages = normalizeArray(filters.pageId);
    selectedPages.forEach((pgId) => {
      const pg = pages.find((p) => p.refId === pgId);
      list.push({ key: "pageId", valId: pgId, label: `Page: ${pg?.name || pgId}` });
    });

    return list;
  };

  const activeFiltersList = getActiveFiltersList();

  const handleRemoveSingleFilter = (key, valId) => {
    setFilters((prev) => {
      if (valId) {
        const norm = normalizeArray(prev[key]);
        const remaining = norm.filter((v) => v !== valId);
        return {
          ...prev,
          [key]: remaining.length > 0 ? remaining : "",
          page: 1,
        };
      }
      const defaultVal = key === "source" ? "all" : (key === "limit" ? 20 : "");
      return {
        ...prev,
        [key]: defaultVal,
        page: 1,
      };
    });
  };

    const buildQuery=()=>{
      const query=new URLSearchParams();

      Object.entries(filters).forEach(([key,val])=>{
        if(val){
          query.append(key,val);
        }
      });

      return query.toString();
    };

  useEffect(()=>{
      if(isMyLeads){
        fetchMyLeads(filters);
      }else if(userId){
        fetchLeadsByUserId( userId, buildQuery() );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },[ isMyLeads, userId, JSON.stringify(filters), refresh]);

    const getLeadDetailsRoute = (lead) => `/lead-details/${lead.source}/${lead._id}`;

    const handleLeadClick = (lead) => {
      try {
        const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        sessionStorage.setItem("user_leads_scroll_pos", scrollY.toString());
        sessionStorage.setItem("last_viewed_lead_id", lead._id);
      } catch (e) {}
      navigate(getLeadDetailsRoute(lead));
    };

    useLayoutEffect(() => {
      try {
        const savedPos = sessionStorage.getItem("user_leads_scroll_pos");
        const lastViewedId = sessionStorage.getItem("last_viewed_lead_id");

        const doScroll = () => {
          let scrolledByElement = false;
          if (lastViewedId) {
            const el = document.getElementById(`lead-card-${lastViewedId}`);
            if (el) {
              el.scrollIntoView({ block: "center", behavior: "instant" });
              scrolledByElement = true;
            }
          }
          if (!scrolledByElement && savedPos !== null) {
            const scrollY = parseInt(savedPos, 10);
            window.scrollTo(0, scrollY);
            document.documentElement.scrollTop = scrollY;
            document.body.scrollTop = scrollY;
            const mainContent = document.querySelector(".main-content") || document.querySelector("#layout-wrapper");
            if (mainContent) mainContent.scrollTop = scrollY;
          }
        };

        doScroll();
        requestAnimationFrame(doScroll);
        setTimeout(doScroll, 50);
        setTimeout(doScroll, 150);
        setTimeout(doScroll, 300);

        setTimeout(() => {
          try {
            sessionStorage.removeItem("user_leads_scroll_pos");
            sessionStorage.removeItem("last_viewed_lead_id");
          } catch (e) {}
        }, 500);
      } catch (e) {}
    }, [leads]);

    const formatDateTime = (date) => {
      if (!date) return { date: "-", time: "-" };

      const d = new Date(date);

      return {
        date: d.toLocaleDateString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
        time: d.toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };
    };
  return (
    <div className="page-content">
      <Breadcrumbs title="Leads" breadcrumbItem={ isMyLeads ? "My Leads" : `${userName || "User"}'s Leads` } />

      <div className="card p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div style={{ minWidth: "260px", maxWidth: "400px", width: "100%" }}>
            <div className="position-relative">
              <i
                className="mdi mdi-magnify"
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "12px",
                  fontSize: "16px",
                  color: "#74788d",
                }}
              />
              <DebouncedSearchInput
                placeholder="Search name / email / phone..."
                value={filters.search}
                style={{ paddingLeft: "35px" }}
                onChange={(val) => handleChange("search", val)}
                delay={300}
              />
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div
              className="px-2 py-1 rounded small fw-semibold"
              style={{
                background: "#f8f9fa",
                border: "1px solid #e9ebec",
                minWidth: "70px",
                textAlign: "center",
              }}
            >
              Leads : {pagination?.total || 0}
            </div>

            <Input
              type="select"
              value={filters.limit}
              style={{ width: "95px" }}
              onChange={(e) => handleChange("limit", e.target.value)}
            >
              {[10, 20, 50, 100, 250, 500, 1000, "all"].map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "All" : v}
                </option>
              ))}
            </Input>

            <Button
              color={activeFiltersList.length > 0 ? "primary" : "light"}
              onClick={() => setFilterModal(true)}
              title="Filter Leads"
              className="d-flex align-items-center gap-1"
            >
              <i className="mdi mdi-filter-variant" />
              {activeFiltersList.length > 0 && (
                <span className="badge bg-white text-primary rounded-circle" style={{ fontSize: "11px" }}>
                  {activeFiltersList.length}
                </span>
              )}
            </Button>

            <Button color="light" onClick={()=>{ handleReset(); setRefresh(p=>!p); }}>
              <i className="mdi mdi-refresh" />
            </Button>

            <Button
              color="success"
              onClick={() => setCreateModal(true)}
              className="d-flex align-items-center gap-1 fw-semibold"
              title="Create Lead"
            >
              <i className="mdi mdi-plus" />
              Create Lead
            </Button>
          </div>
        </div>
      </div>

      {/* 🔥 ACTIVE APPLIED FILTERS CHIPS BAR */}
      {activeFiltersList.length > 0 && (
        <div className="card p-2 mb-3 bg-light border-0 shadow-sm">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <div className="fw-semibold text-muted small me-1 d-flex align-items-center gap-1">
              <i className="mdi mdi-filter-check-outline text-primary fs-6" />
              Applied Filters:
            </div>

            {activeFiltersList.map((item) => (
              <span
                key={item.key + (item.valId || "")}
                className="badge bg-white text-primary border border-primary-subtle px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2 shadow-sm"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                {item.label}
                <i
                  className="mdi mdi-close-circle cursor-pointer text-danger opacity-75"
                  style={{ fontSize: "14px", cursor: "pointer" }}
                  onClick={() => handleRemoveSingleFilter(item.key, item.valId)}
                  title={`Remove ${item.label}`}
                />
              </span>
            ))}

            <Button
              color="link"
              size="sm"
              className="text-danger text-decoration-none p-0 ms-2 fw-semibold small d-flex align-items-center gap-1"
              onClick={() => { handleReset(); setRefresh((p) => !p); }}
            >
              <i className="mdi mdi-trash-can-outline" /> Clear All
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardBody>
          <div className="mb-3">
            <h4 className="mb-0"> { isMyLeads ? "My Leads" : "Assigned User Leads" } </h4>
          </div>

          {
            isMobile ? (
              <MobileLeadCards
                leads={leads}
                loading={loading}
                navigate={navigate}
                onLeadClick={handleLeadClick}
                selectedLeadIds={[]}
                handleLeadSelect={()=>{}}
                hideCheckbox
              />
            ) : (
              <Table className="table align-middle table-hover" style={{fontSize:"12px"}}>
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Project</th>
                    <th>Stage</th>
                    <th>Remark</th>
                    <th>Came Date</th>
                    <th>Quick Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5" style={{ minHeight: "450px" }}>
                        <div style={{ minHeight: "350px" }} className="d-flex align-items-center justify-content-center">
                          <AppLoader compact />
                        </div>
                      </td>
                    </tr>
                  ) : leads?.length ? (
                    leads.map((lead)=>(
                      <tr
                        key={lead._id}
                        id={`lead-card-${lead._id}`}
                        onClick={()=>handleLeadClick(lead)}
                        style={{cursor:"pointer"}}
                      >
                        <td>{lead.name || "-"} <br/>
                          {
                            lead.source === "google" ? (
                              <div className="d-flex align-items-center gap-1 fw-semibold text-danger">
                                <i className="mdi mdi-google" />
                                Google
                              </div>
                            ) : lead.source === "facebook" ? (
                              <>
                                <div className="d-flex align-items-center gap-1 fw-semibold text-info mb-1">
                                  <i className="mdi mdi-facebook" />
                                  Facebook
                                </div>
                              </>
                            ) : (
                              <div className="d-flex align-items-center gap-1 fw-semibold text-success mb-1">
                                <i
                                  className={
                                    lead.source === "ai_calling"
                                      ? "mdi mdi-robot text-primary"
                                      : lead.source === "ivr_calling"
                                      ? "mdi mdi-phone-in-talk text-warning"
                                      : lead.source === "rcs_msg"
                                      ? "mdi mdi-message-processing text-info"
                                      : lead.source === "whatsapp"
                                      ? "mdi mdi-whatsapp text-success"
                                      : "mdi mdi-account-plus-outline"
                                  }
                                />
                                {lead.source === "generic"
                                  ? "Generic Lead"
                                  : lead.source === "ai_calling"
                                  ? "Ai calling"
                                  : lead.source === "ivr_calling"
                                  ? "Ivr calling"
                                  : lead.source === "rcs_msg"
                                  ? "RCS msg"
                                  : lead.source === "whatsapp"
                                  ? "Whatsapp"
                                  : String(lead.source).replace(/_/g, " ").toUpperCase()}
                              </div>
                            )
                          }
                        </td>

                        <td>
                          {lead.email || "-"}
                          <br/>
                          {lead.phone || "-"}
                        </td>

                        <td>
                          {lead.projectName || lead.projectId?.projectName || "-"}
                        </td>
                        <td>
                          <div onClick={(e) => e.stopPropagation()}>
                            <Input
                              type="select"
                              bsSize="sm"
                              className="form-select-sm fw-semibold text-primary border-primary-subtle"
                              style={{ width: "135px", fontSize: "11px" }}
                              value={lead.stageId || ""}
                              onChange={(e) => openStageChangeModal(e, lead, e.target.value)}
                            >
                              <option value="" disabled>{lead.stage || "Select Stage"}</option>
                              {stages.map((stg) => (
                                <option key={stg._id} value={stg._id}>
                                  {stg.name}
                                </option>
                              ))}
                            </Input>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-warning text-dark">
                            {lead.remark || "-"}
                          </span>
                        </td>

                         <td onClick={()=>navigate(getLeadDetailsRoute(lead))} style={{ minWidth:"130px" }} >
                          { lead.createdTime ? (() => {
                                  const dt = formatDateTime(lead.createdTime);
                                  return (
                                    <>
                                      <div className="fw-semibold">{dt.date}</div>
                                      <small className="text-muted">
                                        <i className="mdi mdi-clock-outline me-1"/>
                                        {dt.time}
                                      </small>
                                    </>
                                  );
                                })()
                              : "-"
                          }
                        </td>

                        <td onClick={(e) => e.stopPropagation()} style={{ minWidth: "150px" }}>
                          <div className="d-flex align-items-center gap-1">
                            <Button
                              color="success"
                              size="sm"
                              outline
                              className="p-1 px-2 d-flex align-items-center gap-1"
                              title="Call Lead"
                              onClick={(e) => handleCallLead(e, lead)}
                            >
                              <i className="mdi mdi-phone" />
                            </Button>

                            <Button
                              color="success"
                              size="sm"
                              className="p-1 px-2 d-flex align-items-center gap-1"
                              title="Send WhatsApp Message"
                              onClick={(e) => handleWhatsappLead(e, lead)}
                            >
                              <i className="mdi mdi-whatsapp" />
                            </Button>

                            <Button
                              color="info"
                              size="sm"
                              outline
                              className="p-1 px-2 d-flex align-items-center gap-1"
                              title="View Manager Activity Log"
                              onClick={(e) => openActivityModal(e, lead)}
                            >
                              <i className="mdi mdi-history" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        No Leads Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )
          }

          <LeadFilters
            isOpen={filterModal}
            toggle={() => setFilterModal(false)}
            filters={filters}
            setFilters={setFilters}
            stages={stages}
            projects={projects}
            forms={forms}
            pages={pages}
            hideAssignedToFilter={true}
          />

          <LeadActivityModal
            isOpen={activityModal.isOpen}
            toggle={() => setActivityModal({ isOpen: false, lead: null })}
            source={activityModal.lead?.source}
            leadId={activityModal.lead?._id}
            leadName={activityModal.lead?.name}
          />

          <Modal
            isOpen={stageModal.isOpen}
            toggle={() => setStageModal({ isOpen: false, lead: null, newStageId: "", newStageName: "" })}
            centered
          >
            <ModalHeader toggle={() => setStageModal({ isOpen: false, lead: null, newStageId: "", newStageName: "" })}>
              Update Lead Stage & Add Activity Comment
            </ModalHeader>
            <ModalBody>
              <h5 className="mb-1 text-primary">{stageModal.lead?.name || "Lead"}</h5>
              <div className="d-flex align-items-center gap-2 my-3 p-2 bg-light rounded">
                <span className="badge bg-secondary">{stageModal.lead?.stage || "Current Stage"}</span>
                <i className="mdi mdi-arrow-right-bold text-primary fs-5" />
                <span className="badge bg-primary">{stageModal.newStageName}</span>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Comment / Activity Note <span className="text-danger">*</span>
                </label>
                <Input
                  type="textarea"
                  rows="3"
                  placeholder="Enter reason or details for stage change (e.g., Client scheduled site visit for Sunday)..."
                  value={stageComment}
                  onChange={(e) => setStageComment(e.target.value)}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                color="light"
                onClick={() => setStageModal({ isOpen: false, lead: null, newStageId: "", newStageName: "" })}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={updatingStage || !stageComment.trim()}
                onClick={handleConfirmStageUpdate}
              >
                {updatingStage ? <Spinner size="sm" /> : "Save Stage & Comment"}
              </Button>
            </ModalFooter>
          </Modal>

          <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
            <Button
              size="sm"
              color="light"
              disabled={pagination?.page <= 1}
              onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
            >
              <i className="mdi mdi-chevron-left" />
            </Button>

            <span className="small">
              Page {pagination?.page || 1} / {pagination?.totalPages || 1}
            </span>

            <Button
              size="sm"
              color="light"
              disabled={pagination?.page >= pagination?.totalPages}
              onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
            >
              <i className="mdi mdi-chevron-right" />
            </Button>
          </div>
        </CardBody>
      </Card>

      <CreateLeadModal
        isOpen={createModal}
        toggle={() => setCreateModal(false)}
        onSuccess={() => setRefresh((p) => !p)}
      />
    </div>
  );
};

export default UserLeads;
